import { LayoutSizeContext } from 'App';
import { buildSelectOptions } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WeatherWidgetDetails from 'features/Weather/components/WeatherWidgetDetails/WeatherWidgetDetails';
import WeatherWidgetSelector from 'features/Weather/components/WeatherWidgetSelector/WeatherWidgetSelector';
import useLocation from 'hooks/useLocation';
import { useContext, useMemo, useState } from 'react';
import { useGetDetailedMarine7Query } from 'services/weather/marine/marine.api';
import { joinClasses } from 'utils/componentStyles/style.utils';
import MarineWidgetSelect from '../../components/MarineWidgetSelect/MarineWidgetSelect';
import BeaufortIcons from '../../features/WaveAndWind/components/BeaufortIcons/BeaufortIcons';
import {
  getActiveMarineWidgetItem,
  getActiveMarineWidgetLocation,
  getMarineWidgetActiveItemIndex,
  getMarineWidgetErrorMessage,
  getMarineWidgetLocationEntries,
  getMarineWidgetLocationOptions,
  getMarineWidgetLocations,
} from '../../utils/marineForecastWidget.utils';
import './assets/waveAndWindWidget.scss';
import { details, layoutConfig, route } from './config/waveAndWindWidget.config';
import {
  getWaveAndWindWidgetDays,
  getWaveAndWindWidgetLayout,
  getWaveAndWindWidgetSegmentEntries,
  getWaveAndWindWidgetTileAriaLabel,
  getWaveAndWindWidgetTileValue,
} from './utils/waveAndWindWidget.utils';

const WaveAndWindWidget = ({ layout, dimensions, containerSizePx }) => {
  const layoutSize = useContext(LayoutSizeContext);
  const { locations } = useLocation();
  const {
    data: detailedMarine = [],
    isLoading: isLoadingDetailedMarine,
    isFetching: isFetchingDetailedMarine,
    isError: isDetailedMarineError,
    error: detailedMarineError,
    refetch: refetchDetailedMarine,
  } = useGetDetailedMarine7Query(locations.effectiveLocation);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(0);

  const marineLocations = useMemo(() => {
    return getMarineWidgetLocations(detailedMarine);
  }, [detailedMarine]);
  const locationOptions = useMemo(
    () => getMarineWidgetLocationOptions(marineLocations),
    [marineLocations],
  );

  const activeLocation = getActiveMarineWidgetLocation(marineLocations, selectedLocationId);

  const locationEntries = useMemo(() => {
    return getMarineWidgetLocationEntries(detailedMarine, activeLocation);
  }, [detailedMarine, activeLocation]);

  const forecastDays = useMemo(() => getWaveAndWindWidgetDays(locationEntries), [locationEntries]);
  const dayOptions = useMemo(() => buildSelectOptions(forecastDays), [forecastDays]);

  const activeDay = getActiveMarineWidgetItem(forecastDays, selectedDay) ?? '';
  const segmentEntries = useMemo(
    () => getWaveAndWindWidgetSegmentEntries(locationEntries, activeDay),
    [locationEntries, activeDay],
  );
  const activeSegmentIndex = getMarineWidgetActiveItemIndex(segmentEntries, selectedSegmentIndex);
  const selectedSegment = segmentEntries[activeSegmentIndex];
  const config = layoutConfig[layout] ?? layoutConfig['1x1'];
  const widgetLayout = getWaveAndWindWidgetLayout({
    containerSizePx,
    layoutSize,
    dimensions,
  });
  const isRefreshing = isFetchingDetailedMarine && !isLoadingDetailedMarine;

  if (isDetailedMarineError && !isLoadingDetailedMarine) {
    return (
      <ErrorMessage
        message={getMarineWidgetErrorMessage(
          detailedMarineError,
          'Unable to load wave and wind conditions.',
        )}
        retryRoute={route}
        onRetry={() => refetchDetailedMarine()}
      />
    );
  }

  if (isLoadingDetailedMarine) {
    return <WidgetLoadingAnimation widgetName="WaveAndWindConditions" dimensions={dimensions} />;
  }

  if (!forecastDays.length || !segmentEntries.length) {
    return (
      <section aria-label="Wave and wind conditions widget">
        <WidgetTimeUpdate route={route} compact={config.compactTimeUpdate} />
        <p className="p-3 mb-0" role="status">
          No wave and wind forecast data found.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="Wave and wind conditions widget"
      aria-busy={isRefreshing || undefined}
      className={joinClasses('wave-and-wind-widget', config.wrapperClassName)}
      data-layout={layout}
    >
      <WidgetTimeUpdate route={route} compact={config.compactTimeUpdate} />
      {isRefreshing && (
        <span className="visually-hidden" role="status">
          Updating wave and wind conditions.
        </span>
      )}

      <div className={config.contentClassName}>
        <div className={config.controlsClassName}>
          {locationOptions.length > 0 && (
            <MarineWidgetSelect
              label="Location"
              options={locationOptions}
              selectedValue={activeLocation?.id}
              placeholder="Select a location"
              isSearchable
              onSelect={(locationId) => {
                setSelectedLocationId(locationId ?? null);
                setSelectedDay('');
                setSelectedSegmentIndex(0);
              }}
            />
          )}

          <MarineWidgetSelect
            label="Forecast day"
            options={dayOptions}
            selectedValue={activeDay}
            placeholder="Select a day"
            isSearchable
            onSelect={(day) => {
              setSelectedDay(day ?? '');
              setSelectedSegmentIndex(0);
            }}
          />
        </div>

        <div
          className="pt-2"
          style={{
            flex: `0 0 ${config.selectorHeight}`,
            height: config.selectorHeight,
            minHeight: 0,
          }}
        >
          <WeatherWidgetSelector
            items={segmentEntries}
            selectedIndex={activeSegmentIndex}
            visibleNumOfItems={config.visibleCount(widgetLayout)}
            selectorMode={config.selectorMode}
            gridColumnCount={config.gridColumnCount}
            gap={config.selectorGap}
            arrowButtonWidth={widgetLayout.arrowButtonWidth}
            arrowIconSize={widgetLayout.arrowIconSize}
            listContainerStyle={config.selectorListStyle}
            tileStyle={config.selectorTileStyle}
            ariaLabel="Wave and wind forecast times of day"
            previousButtonLabel="Show previous marine forecast time"
            nextButtonLabel="Show next marine forecast time"
            onSelect={setSelectedSegmentIndex}
            getKey={(entry) => entry?.iso8601}
            getTileProps={(entry) => ({
              label: entry?.day_segment,
              value: getWaveAndWindWidgetTileValue(entry),
              ariaLabel: getWaveAndWindWidgetTileAriaLabel(entry),
              icon: (
                <span
                  aria-hidden="true"
                  className="d-flex h-100 w-100 align-items-center justify-content-center"
                >
                  <BeaufortIcons conditionId={entry?.beaufort} />
                </span>
              ),
              iconStyle: {
                width:
                  config.selectorMode === 'row'
                    ? widgetLayout.rowTileIconSize
                    : widgetLayout.tileIconSize,
                height:
                  config.selectorMode === 'row'
                    ? widgetLayout.rowTileIconSize
                    : widgetLayout.tileIconSize,
              },
              labelStyle: widgetLayout.tileTextStyle,
              valueStyle: widgetLayout.tileTextStyle,
            })}
          />
        </div>

        <WeatherWidgetDetails
          selectedItem={selectedSegment}
          details={details}
          layoutGroups={config.detailGroups}
          containerClassName={config.detailsClassName}
          containerStyle={config.detailsStyle({ layoutSize })}
          iconSize={config.detailsIconSize}
          ariaLabel={`Wave and wind details for ${activeDay} ${selectedSegment?.day_segment ?? ''}`}
        />
      </div>
    </section>
  );
};

export default WaveAndWindWidget;
